import { Component, ChangeDetectionStrategy, computed, input, output, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { appVersion as APP_VERSION } from '../../../environments/version';
import { environment } from '../../../environments/environment';
import { AccessService } from '../../core/services/access.service';
import { FeatureService } from '../../core/services/feature.service';
import { NavigatorLoginService } from '../../core/services/navigator-login.service';

interface NavItem extends MenuItem {
  svgIcon?: {
    viewBox: string;
    paths: string[];
    /** Extra paths drawn on top of `paths` with their own explicit fill, ignoring `currentColor` — for a badge (e.g. a background circle + glyph) that must stay legible regardless of the item's hover/active color. `transform` lets a `d` keep its own well-formed local coordinate space (e.g. a known-good icon path lifted as-is) and be scaled/positioned onto the badge via an SVG transform instead of hand-recalculating every curve's control points. */
    overlay?: { d: string; fill: string; transform?: string }[];
    /** Overrides the shared `w-[22.4px] h-[22.4px]` render size. Use when `viewBox` is padded larger than the artwork itself (e.g. to make room for a badge) — scale this up by the same ratio as the padding so the artwork keeps rendering at the same pixel size as every other nav icon instead of shrinking to fit the padded canvas. */
    sizeClass?: string;
  };
  iconImage?: string;
  /** Tailwind text-color class applied to a `pi pi-*` icon, overriding the uniform
   *  `.sidenav-icon-item { color: var(--text-color-secondary) }`. The only per-item color escape
   *  hatch for a font icon — `svgIcon.overlay[].fill` covers SVGs, and `iconImage` items carry their
   *  own colors. Deliberately wins over the hover/active `color` too, matching how the `iconImage`
   *  logos stay their brand color; background, scale and the active indicator bar still respond. */
  iconColorClass?: string;
  requiresSigmaAccess?: boolean;
  requiresGrafanaOnPremAccess?: boolean;
  requiresAdminAccess?: boolean;
  requiresEllaAccess?: boolean;
  requiresScarlettAccess?: boolean;
  requiresNavigatorAccess?: boolean;
  /** Draws a separator line below this item, grouping it with the ones above. Set it on the LAST
   *  item of a group rather than adding a standalone separator entry — the access filter removes
   *  hidden items, and a separator that is its own entry would survive and leave a stray line for a
   *  user who cannot see the group it was meant to close. */
  dividerAfter?: boolean;
  /** Path used to determine active/highlighted state, when it differs from the routerLink target (e.g. a section landing route). Defaults to routerLink. */
  activeMatch?: string;
}

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [CommonModule, DrawerModule, TooltipModule, RouterLink],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavComponent {
  private router = inject(Router);
  private accessService = inject(AccessService);
  private featureService = inject(FeatureService);
  private navigatorLoginService = inject(NavigatorLoginService);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  appVersion = APP_VERSION;
  environmentName = environment.environmentName;
  visible = input<boolean>(true);
  isMobile = input<boolean>(false);
  visibleChange = output<boolean>();
  navigate = output<void>();

  private readonly allNavItems: NavItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      routerLink: ['/dashboard'],
      command: () => this.onNavigate()
    },
    {
      label: 'Ella',
      icon: 'pi pi-sitemap',
      // Same purple as Scarlett's, so the two related platform sections read as a pair.
      iconColorClass: 'text-(--p-galaxy-accent-purple)',
      routerLink: ['/ella/overview'],
      activeMatch: '/ella',
      command: () => this.onNavigate(),
      requiresEllaAccess: true,
    },
    {
      label: 'Scarlett',
      icon: 'pi pi-shield',
      // Purple to read as Ella-adjacent without being Ella. `--p-galaxy-accent-purple` (#8b5cf6) is
      // the existing token, already described in app.config.ts as being for category icon chips and
      // admin features.
      iconColorClass: 'text-(--p-galaxy-accent-purple)',
      routerLink: ['/scarlett/overview'],
      activeMatch: '/scarlett',
      command: () => this.onNavigate(),
      // Same basis as Ella's, matching the server: Scarlett grants `view` to any Ella user, so
      // visibility here and reachability there agree. What a user may DO inside is a separate
      // question, answered by GET /api/v1/scarlett/capabilities rather than by any group check.
      requiresScarlettAccess: true,
      // Closes the Ella/Scarlett platform group. Attached to the LAST item of the group rather than
      // being its own entry so it disappears with them — a standalone separator would survive the
      // access filter and leave a stray line for a user who can see neither.
      dividerAfter: true,
    },
    {
      label: 'Sigma Dashboard',
      svgIcon: {
        viewBox: '0 0 35 32',
        paths: [
          'M14.4523 15.3524L0 0.833252L21.4587 7.77987L14.4523 15.3524Z',
          'M15.2997 16.1905L26.9131 30.6246L22.2956 8.60604L15.2997 16.1905Z',
          'M25.4971 30.751L9.08954 23.5399L19.6969 23.5435L25.4971 30.751Z',
          'M27.84 0.988769L22.4184 6.85587L17.1924 5.16107L27.84 0.988769Z',
          'M25.9213 4.64943L28.9927 1.49197L34.2844 4.80138L25.9213 4.64943Z'
        ]
      },
      routerLink: ['/sigma-dashboard'],
      command: () => this.onNavigate(),
      requiresSigmaAccess: true,
    },
    {
      label: 'Grafana Dashboard (On-Prem)',
      svgIcon: {
        // Canvas is bigger than the logo's own 0-24 coordinate space (below) purely to give the
        // badge room at the logo's bottom-right corner (24,24) without clipping — the extra
        // 0-24..32 margin on the right/bottom is transparent, and the Grafana paths themselves
        // are untouched (same coordinates as the plain mark), so the logo artwork isn't rescaled.
        viewBox: '0 0 32 32',
        // 37.33px would exactly preserve the mark's 28px scale against this padded 32-unit canvas
        // (28 * 32/24), but that read as too large next to the rest of the nav — shrunk 20% to
        // 29.87px. Deliberately excluded from the nav-wide 20% icon shrink (unlike every other
        // icon here, including the Grafana Cloud one below) so it no longer matches Cloud's box.
        sizeClass: 'w-[29.87px] h-[29.87px]',
        paths: [
          'M23.02 10.59a8.578 8.578 0 0 0-.862-3.034 8.911 8.911 0 0 0-1.789-2.445c.337-1.342-.413-2.505-.413-2.505-1.292-.08-2.113.4-2.416.62-.052-.02-.102-.044-.154-.064-.22-.089-.446-.172-.677-.247-.231-.073-.47-.14-.711-.197a9.867 9.867 0 0 0-.875-.161C14.557.753 12.94 0 12.94 0c-1.804 1.145-2.147 2.744-2.147 2.744l-.018.093c-.098.029-.2.057-.298.088-.138.042-.275.094-.413.143-.138.055-.275.107-.41.166a8.869 8.869 0 0 0-1.557.87l-.063-.029c-2.497-.955-4.716.195-4.716.195-.203 2.658.996 4.33 1.235 4.636a11.608 11.608 0 0 0-.607 2.635C1.636 12.677.953 15.014.953 15.014c1.926 2.214 4.171 2.351 4.171 2.351.003-.002.006-.002.006-.005.285.509.615.994.986 1.446.156.19.32.371.488.548-.704 2.009.099 3.68.099 3.68 2.144.08 3.553-.937 3.849-1.173a9.784 9.784 0 0 0 3.164.501h.08l.055-.003.107-.002.103-.005.003.002c1.01 1.44 2.788 1.646 2.788 1.646 1.264-1.332 1.337-2.653 1.337-2.94v-.058c0-.02-.003-.039-.003-.06.265-.187.52-.387.758-.6a7.875 7.875 0 0 0 1.415-1.7c1.43.083 2.437-.885 2.437-.885-.236-1.49-1.085-2.216-1.264-2.354l-.018-.013-.016-.013a.217.217 0 0 1-.031-.02c.008-.092.016-.18.02-.27.011-.162.016-.323.016-.48v-.253l-.005-.098-.008-.135a1.891 1.891 0 0 0-.01-.13c-.003-.042-.008-.083-.013-.125l-.016-.124-.018-.122a6.215 6.215 0 0 0-2.032-3.73 6.015 6.015 0 0 0-3.222-1.46 6.292 6.292 0 0 0-.85-.048l-.107.002h-.063l-.044.003-.104.008a4.777 4.777 0 0 0-3.335 1.695c-.332.4-.592.84-.768 1.297a4.594 4.594 0 0 0-.312 1.817l.003.091c.005.055.007.11.013.164a3.615 3.615 0 0 0 .698 1.82 3.53 3.53 0 0 0 1.827 1.282c.33.098.66.14.971.137.039 0 .078 0 .114-.002l.063-.003c.02 0 .041-.003.062-.003.034-.002.065-.007.099-.01.007 0 .018-.003.028-.003l.031-.005.06-.008a1.18 1.18 0 0 0 .112-.02c.036-.008.072-.013.109-.024a2.634 2.634 0 0 0 .914-.415c.028-.02.056-.041.085-.065a.248.248 0 0 0 .039-.35.244.244 0 0 0-.309-.06l-.078.042c-.09.044-.184.083-.283.116a2.476 2.476 0 0 1-.475.096c-.028.003-.054.006-.083.006l-.083.002c-.026 0-.054 0-.08-.002l-.102-.006h-.012l-.024.006c-.016-.003-.031-.003-.044-.006-.031-.002-.06-.007-.091-.01a2.59 2.59 0 0 1-.724-.213 2.557 2.557 0 0 1-.667-.438 2.52 2.52 0 0 1-.805-1.475 2.306 2.306 0 0 1-.029-.444l.006-.122v-.023l.002-.031c.003-.021.003-.04.005-.06a3.163 3.163 0 0 1 1.352-2.29 3.12 3.12 0 0 1 .937-.43 2.946 2.946 0 0 1 .776-.101h.06l.07.002.045.003h.026l.07.005a4.041 4.041 0 0 1 1.635.49 3.94 3.94 0 0 1 1.602 1.662 3.77 3.77 0 0 1 .397 1.414l.005.076.003.075c.002.026.002.05.002.075 0 .024.003.052 0 .07v.065l-.002.073-.008.174a6.195 6.195 0 0 1-.08.639 5.1 5.1 0 0 1-.267.927 5.31 5.31 0 0 1-.624 1.13 5.052 5.052 0 0 1-3.237 2.014 4.82 4.82 0 0 1-.649.066l-.039.003h-.287a6.607 6.607 0 0 1-1.716-.265 6.776 6.776 0 0 1-3.4-2.274 6.75 6.75 0 0 1-.746-1.15 6.616 6.616 0 0 1-.714-2.596l-.005-.083-.002-.02v-.056l-.003-.073v-.096l-.003-.104v-.07l.003-.163c.008-.22.026-.45.054-.678a8.707 8.707 0 0 1 .28-1.355c.128-.444.286-.872.473-1.277a7.04 7.04 0 0 1 1.456-2.1 5.925 5.925 0 0 1 .953-.763c.169-.111.343-.213.524-.306.089-.05.182-.091.273-.135.047-.02.093-.042.138-.062a7.177 7.177 0 0 1 .714-.267l.145-.045c.049-.015.098-.026.148-.041.098-.029.197-.052.296-.076.049-.013.1-.02.15-.033l.15-.032.151-.028.076-.013.075-.01.153-.024c.057-.01.114-.013.171-.023l.169-.021c.036-.003.073-.008.106-.01l.073-.008.036-.003.042-.002c.057-.003.114-.008.171-.01l.086-.006h.023l.037-.003.145-.007a7.999 7.999 0 0 1 1.708.125 7.917 7.917 0 0 1 2.048.68 8.253 8.253 0 0 1 1.672 1.09l.09.077.089.078c.06.052.114.107.171.159.057.052.112.106.166.16.052.055.107.107.159.164a8.671 8.671 0 0 1 1.41 1.978c.012.026.028.052.04.078l.04.078.075.156c.023.051.05.1.07.153l.065.15a8.848 8.848 0 0 1 .45 1.34.19.19 0 0 0 .201.142.186.186 0 0 0 .172-.184c.01-.246.002-.532-.024-.856z'
        ],
        // Bottom-right badge: an opaque black circle so the white house glyph on top of it never
        // clashes with the Grafana mark underneath, regardless of hover/active color changes.
        // Centered exactly on (24,24) — the logo's own bottom-right corner — now that the viewBox
        // above gives it room to sit there without clipping.
        overlay: [
          { d: 'M16.95,24 A7.05,7.05 0 1,0 31.05,24 A7.05,7.05 0 1,0 16.95,24 Z', fill: '#000000' },
          { d: 'M24,18.975 L28.5,23.025 L28.5,29.025 L19.5,29.025 L19.5,23.025 Z', fill: '#ffffff' }
        ]
      },
      routerLink: ['/grafana-on-prem-dashboard'],
      command: () => this.onNavigate(),
      requiresGrafanaOnPremAccess: true,
    },
    {
      label: 'Grafana Dashboard (Cloud)',
      svgIcon: {
        viewBox: '0 0 24 24',
        // Was 29.87px (matching the on-prem icon's box) before the nav-wide 20% icon shrink; the
        // on-prem icon was deliberately excluded from that shrink, so the two no longer match.
        sizeClass: 'w-[23.9px] h-[23.9px]',
        paths: [
          // Same Grafana mark as the entry above, unchanged, so this still reads as "Grafana" first.
          'M23.02 10.59a8.578 8.578 0 0 0-.862-3.034 8.911 8.911 0 0 0-1.789-2.445c.337-1.342-.413-2.505-.413-2.505-1.292-.08-2.113.4-2.416.62-.052-.02-.102-.044-.154-.064-.22-.089-.446-.172-.677-.247-.231-.073-.47-.14-.711-.197a9.867 9.867 0 0 0-.875-.161C14.557.753 12.94 0 12.94 0c-1.804 1.145-2.147 2.744-2.147 2.744l-.018.093c-.098.029-.2.057-.298.088-.138.042-.275.094-.413.143-.138.055-.275.107-.41.166a8.869 8.869 0 0 0-1.557.87l-.063-.029c-2.497-.955-4.716.195-4.716.195-.203 2.658.996 4.33 1.235 4.636a11.608 11.608 0 0 0-.607 2.635C1.636 12.677.953 15.014.953 15.014c1.926 2.214 4.171 2.351 4.171 2.351.003-.002.006-.002.006-.005.285.509.615.994.986 1.446.156.19.32.371.488.548-.704 2.009.099 3.68.099 3.68 2.144.08 3.553-.937 3.849-1.173a9.784 9.784 0 0 0 3.164.501h.08l.055-.003.107-.002.103-.005.003.002c1.01 1.44 2.788 1.646 2.788 1.646 1.264-1.332 1.337-2.653 1.337-2.94v-.058c0-.02-.003-.039-.003-.06.265-.187.52-.387.758-.6a7.875 7.875 0 0 0 1.415-1.7c1.43.083 2.437-.885 2.437-.885-.236-1.49-1.085-2.216-1.264-2.354l-.018-.013-.016-.013a.217.217 0 0 1-.031-.02c.008-.092.016-.18.02-.27.011-.162.016-.323.016-.48v-.253l-.005-.098-.008-.135a1.891 1.891 0 0 0-.01-.13c-.003-.042-.008-.083-.013-.125l-.016-.124-.018-.122a6.215 6.215 0 0 0-2.032-3.73 6.015 6.015 0 0 0-3.222-1.46 6.292 6.292 0 0 0-.85-.048l-.107.002h-.063l-.044.003-.104.008a4.777 4.777 0 0 0-3.335 1.695c-.332.4-.592.84-.768 1.297a4.594 4.594 0 0 0-.312 1.817l.003.091c.005.055.007.11.013.164a3.615 3.615 0 0 0 .698 1.82 3.53 3.53 0 0 0 1.827 1.282c.33.098.66.14.971.137.039 0 .078 0 .114-.002l.063-.003c.02 0 .041-.003.062-.003.034-.002.065-.007.099-.01.007 0 .018-.003.028-.003l.031-.005.06-.008a1.18 1.18 0 0 0 .112-.02c.036-.008.072-.013.109-.024a2.634 2.634 0 0 0 .914-.415c.028-.02.056-.041.085-.065a.248.248 0 0 0 .039-.35.244.244 0 0 0-.309-.06l-.078.042c-.09.044-.184.083-.283.116a2.476 2.476 0 0 1-.475.096c-.028.003-.054.006-.083.006l-.083.002c-.026 0-.054 0-.08-.002l-.102-.006h-.012l-.024.006c-.016-.003-.031-.003-.044-.006-.031-.002-.06-.007-.091-.01a2.59 2.59 0 0 1-.724-.213 2.557 2.557 0 0 1-.667-.438 2.52 2.52 0 0 1-.805-1.475 2.306 2.306 0 0 1-.029-.444l.006-.122v-.023l.002-.031c.003-.021.003-.04.005-.06a3.163 3.163 0 0 1 1.352-2.29 3.12 3.12 0 0 1 .937-.43 2.946 2.946 0 0 1 .776-.101h.06l.07.002.045.003h.026l.07.005a4.041 4.041 0 0 1 1.635.49 3.94 3.94 0 0 1 1.602 1.662 3.77 3.77 0 0 1 .397 1.414l.005.076.003.075c.002.026.002.05.002.075 0 .024.003.052 0 .07v.065l-.002.073-.008.174a6.195 6.195 0 0 1-.08.639 5.1 5.1 0 0 1-.267.927 5.31 5.31 0 0 1-.624 1.13 5.052 5.052 0 0 1-3.237 2.014 4.82 4.82 0 0 1-.649.066l-.039.003h-.287a6.607 6.607 0 0 1-1.716-.265 6.776 6.776 0 0 1-3.4-2.274 6.75 6.75 0 0 1-.746-1.15 6.616 6.616 0 0 1-.714-2.596l-.005-.083-.002-.02v-.056l-.003-.073v-.096l-.003-.104v-.07l.003-.163c.008-.22.026-.45.054-.678a8.707 8.707 0 0 1 .28-1.355c.128-.444.286-.872.473-1.277a7.04 7.04 0 0 1 1.456-2.1 5.925 5.925 0 0 1 .953-.763c.169-.111.343-.213.524-.306.089-.05.182-.091.273-.135.047-.02.093-.042.138-.062a7.177 7.177 0 0 1 .714-.267l.145-.045c.049-.015.098-.026.148-.041.098-.029.197-.052.296-.076.049-.013.1-.02.15-.033l.15-.032.151-.028.076-.013.075-.01.153-.024c.057-.01.114-.013.171-.023l.169-.021c.036-.003.073-.008.106-.01l.073-.008.036-.003.042-.002c.057-.003.114-.008.171-.01l.086-.006h.023l.037-.003.145-.007a7.999 7.999 0 0 1 1.708.125 7.917 7.917 0 0 1 2.048.68 8.253 8.253 0 0 1 1.672 1.09l.09.077.089.078c.06.052.114.107.171.159.057.052.112.106.166.16.052.055.107.107.159.164a8.671 8.671 0 0 1 1.41 1.978c.012.026.028.052.04.078l.04.078.075.156c.023.051.05.1.07.153l.065.15a8.848 8.848 0 0 1 .45 1.34.19.19 0 0 0 .201.142.186.186 0 0 0 .172-.184c.01-.246.002-.532-.024-.856z'
        ]
      },
      routerLink: ['/grafana-cloud-dashboard'],
      command: () => this.onNavigate(),
      requiresAdminAccess: true,
    },
    {
      label: 'Admin Dashboard',
      icon: 'pi pi-user',
      routerLink: ['/admin-dashboard'],
      command: () => this.onNavigate(),
      requiresAdminAccess: true,
    },
    {
      label: 'Canary Backfill',
      iconImage: '/images/canary_logo_white.png',
      routerLink: ['/canary-backfill'],
      command: () => this.onNavigate(),
      requiresAdminAccess: true,
    },
    {
      label: 'Navigator',
      icon: 'pi pi-compass',
      routerLink: ['/navigator'],
      command: () => this.onNavigatorNavigate(),
      requiresNavigatorAccess: true,
    },
    {
      label: 'EVO Edge',
      iconImage: '/images/evo_edge_logo_white.png',
      routerLink: ['/evo-edge'],
      command: () => this.onNavigate(),
      requiresAdminAccess: true,
    },
    {
      label: 'Asset Trends',
      icon: 'pi pi-chart-line',
      routerLink: ['/asset-trends'],
      command: () => this.onNavigate(),
    }
  ];

  navItems = computed(() =>
    this.allNavItems.filter(item =>
      (!item.requiresSigmaAccess   || (this.accessService.isSigmaUser()   === true && this.featureService.isSigmaDashboardActive()   !== false)) &&
      (!item.requiresGrafanaOnPremAccess || (this.accessService.isGrafanaOnPremUser() === true && this.featureService.isGrafanaOnPremDashboardActive() !== false)) &&
      (!item.requiresAdminAccess     || this.accessService.isGalaxyAdmin()      === true) &&
      (!item.requiresEllaAccess      || this.accessService.hasEllaAccess()      === true) &&
      (!item.requiresScarlettAccess  || this.accessService.hasScarlettAccess()  === true) &&
      (!item.requiresNavigatorAccess || this.accessService.hasNavigatorAccess() === true)
    )
  );

  /**
   * Font-icon classes for a nav item. A single helper rather than the expression inlined twice,
   * because the desktop and mobile templates render icons separately and silently drifting apart is
   * the standing hazard with this component — `iconColorClass` would otherwise be easy to add to one
   * and forget in the other.
   */
  iconClasses(item: NavItem): string {
    return `${item.icon} text-2xl!${item.iconColorClass ? ` ${item.iconColorClass}` : ''}`;
  }

  isActive(item: NavItem): boolean {
    this.currentUrl();
    const path = item.activeMatch ?? (item.routerLink as string[])[0];
    return this.router.isActive(path, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  onNavigate(): void {
    this.navigate.emit();
  }

  /**
   * The Navigator route is lazy-loaded behind an async route guard, so by the time its component
   * activates, we're well past the browser's gesture window for window.open(). Opening the popup
   * here instead -- synchronously, as a direct result of this click -- is the one place in this
   * flow that's actually guaranteed to be in the same task as the user's gesture.
   */
  onNavigatorNavigate(): void {
    this.navigatorLoginService.openPopupFromGesture();
    this.onNavigate();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }
}
